import React from "react";
import { UseCases } from "@planzone/core";
import { Safeguard } from "@scenes/safeguard";
import { Props } from "./types";
import { Context } from "./context";
import { Root } from "./root";
import { useViewElement } from "./utils/view";
import { Resizeable } from "./resizeable";
import { Constrainable } from "./constrainable";
import { List } from "./list";
import { Task } from "./task";
import { Event } from "./event";
import { Milestone } from "./milestone";
import { Overtime } from "./overtime";
import { Derivation } from "./derivation";
import { Progress } from "./progress";
import { Draggable } from "./draggable";
import {
  isBlocked,
  isEvent,
  isList,
  isMilestone,
  isProject,
  isTask,
} from "./utils";

function Element({
  graphRef,
  ctx,
  element,
  minDate,
  dates,
  constraints,
  index,
  project,
  meta,
}: Props) {
  const viewElement = useViewElement(element, project);
  const CurrentElement =
    isList(element) || isProject(element)
      ? List
      : isTask(element)
        ? Task
        : isMilestone(element)
          ? Milestone
          : isEvent(element)
            ? Event
            : () => <React.Fragment />;

  return (
    <Context {...ctx}>
      <Safeguard
        useCases={[
          UseCases.editProject,
          UseCases.editList,
          UseCases.editTask,
          UseCases.editMilestone,
          UseCases.editEvent,
        ]}
      >
        {({
          canEditProject,
          canEditList,
          canEditTask,
          canEditMilestone,
          canEditEvent,
        }) => (
          <Root element={element} minDate={minDate}>
            {({ width, height, dayDiff, offset, isDragging, isHovered }) =>
              (
                isProject(element)
                  ? canEditProject
                  : isList(element)
                    ? canEditList
                    : isTask(element)
                      ? canEditTask
                      : isMilestone(element)
                        ? canEditMilestone
                        : isEvent(element)
                          ? canEditEvent
                          : false
              ) ? (
                <Resizeable
                  element={element}
                  constraints={constraints}
                  graphRef={graphRef}
                  project={project}
                  dates={dates}
                  offset={offset}
                >
                  <Constrainable
                    element={{ ...element, index }}
                    elementWidth={width}
                    constraints={constraints}
                  >
                    <Draggable
                      element={element}
                      graphRef={graphRef}
                      project={project}
                      isDisabled={isBlocked(element, constraints)}
                      dates={dates}
                      dayDiff={dayDiff}
                    >
                      {(handler, cancelDrag) => (
                        <Derivation element={element} isDisabled={isDragging}>
                          {() => (
                            <Overtime
                              element={element}
                              isDisabled={isDragging}
                              limitDate={dates[dates.length - 1]}
                            >
                              {(overtime) => (
                                <Progress
                                  element={element}
                                  progress={meta.percentComplete}
                                >
                                  <CurrentElement
                                    element={element}
                                    height={height}
                                    width={width}
                                    constraints={constraints}
                                    hasOvertime={overtime > 0}
                                    isHovered={isHovered}
                                    onClick={
                                      !isDragging
                                        ? () => {
                                            viewElement();
                                            cancelDrag();
                                          }
                                        : undefined
                                    }
                                    {...handler}
                                  />
                                </Progress>
                              )}
                            </Overtime>
                          )}
                        </Derivation>
                      )}
                    </Draggable>
                  </Constrainable>
                </Resizeable>
              ) : (
                <Derivation element={element} isDisabled={isDragging}>
                  {() => (
                    <Overtime
                      element={element}
                      isDisabled={isDragging}
                      limitDate={dates[dates.length - 1]}
                    >
                      {(overtime) => (
                        <Progress
                          element={element}
                          progress={meta.percentComplete}
                        >
                          <CurrentElement
                            element={element}
                            height={height}
                            width={width}
                            constraints={constraints}
                            hasOvertime={overtime > 0}
                            isHovered={isHovered}
                            onClick={!isDragging ? viewElement : undefined}
                          />
                        </Progress>
                      )}
                    </Overtime>
                  )}
                </Derivation>
              )
            }
          </Root>
        )}
      </Safeguard>
    </Context>
  );
}

export { Element };
